import { motion, AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

// eslint-disable-next-line import/no-named-as-default
import Alert from '@/components/Alert';
import type { Alert as AlertType } from '@/components/Alert';

import * as styles from '@/styles/components/Toast';

type Toast = {
  visible: boolean;
  handleComplete?: () => void;
} & AlertType;

export const useToast = () => {
  const [{ status, title, message }, setToast] = useState<AlertType>({
    status: 'success',
    title: '',
    message: '',
  });

  const visible = useMemo(() => {
    return !!title || !!message;
  }, [message, title]);

  const showToast = ({ status = 'success', title = '', message }: AlertType) => {
    setToast({
      status,
      title,
      message,
    });

    const timer = setTimeout(() => {
      clearTimeout(timer);
      setToast({
        status: 'success',
        title: '',
        message: '',
      });
    }, 3000);
  };

  return [{ visible, status, title, message }, showToast] as const;
};

const Toast = ({ visible = false, handleComplete, ...prop }: Toast) => {
  const visibleStyle = {
    opacity: 1,
    y: 0,
  };
  const hiddenStyle = {
    opacity: 0,
    y: 16,
  };
  const transition = {
    ease: 'circOut',
    duration: 0.3,
  };

  if (typeof window === 'undefined') {
    return null;
  } else {
    return createPortal(
      <AnimatePresence onExitComplete={handleComplete}>
        {visible && (
          <motion.div
            css={styles.root}
            key='toast'
            initial={hiddenStyle}
            animate={visibleStyle}
            exit={hiddenStyle}
            transition={transition}>
            <Alert {...prop} />
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
    );
  }
};

export default Toast;
